import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  Eye,
  MapPin,
  NavigationArrow,
  Image as ImageIcon,
  ChartLineUp,
  ArrowsClockwise,
  TrendUp,
  ArrowUp,
  ArrowDown,
  Minus,
  Sparkle,
  ChatCircleDots,
} from "@phosphor-icons/react";
import Sparkline from "./Sparkline";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { useAuth } from "../lib/auth";

const METRICS = [
  { key: "profile_view", label: "Profile views", icon: Eye },
  { key: "pin_click", label: "Pin clicks", icon: MapPin },
  { key: "directions_click", label: "Directions clicks", icon: NavigationArrow },
  { key: "menu_view", label: "Menu views", icon: ImageIcon },
];

// Pro-only metrics — surfaced separately so non-Pro owners aren't teased
// with tiles they can't unlock data for.
//
// Naming clarity: the legacy "Contact inquiries" is the per-truck
// contact button on the public profile page (`POST /profile/{slug}/contact`).
// The marketplace inquiries from `/find-a-truck` are a separate event
// type ("event_inquiry"). Both surfaced as their own tile so owners can
// tell which surface is driving leads (very different optimisation
// decisions).
const PRO_METRICS = [
  {
    key: "contact_inquiry",
    label: "Profile inquiries",
    icon: ChatCircleDots,
    helper: "From the contact button on your /t/:slug page",
  },
  {
    key: "event_inquiry",
    label: "Event inquiries",
    icon: ChatCircleDots,
    helper: "From the catering marketplace on /find-a-truck",
  },
];
// Legacy single-tile alias kept so prior call-sites still compile.
const PRO_METRIC = PRO_METRICS[0];

/**
 * Owner-facing analytics card. Shows Today and This week side-by-side for the
 * 4 key events, plus the "directions conversion rate" — the percentage of
 * profile views that also clicked Get Directions. Low complexity on purpose:
 * no charts, just numbers the owner can scan in 2 seconds.
 */
export default function AnalyticsCard() {
  const { user } = useAuth();
  const isPro = !!user?.is_pro;
  const [range, setRange] = useState("today"); // "today" | "week"
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  // When set, opens a "this week, day-by-day" breakdown dialog for the
  // metric whose tile was clicked. Mirrors the sparkline data the API
  // already returns so there's no extra round-trip.
  const [breakdownMetric, setBreakdownMetric] = useState(null);

  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const res = await api.get("/analytics/summary");
      setData(res.data);
    } catch (e) {
      // Don't blow up the whole dashboard on a transient summary error —
      // just surface a toast and show a friendly empty state.
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load analytics");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  const totals = data?.totals?.[range] || {
    profile_view: 0, pin_click: 0, directions_click: 0, menu_view: 0,
    contact_inquiry: 0, event_inquiry: 0,
  };
  const rate = data?.totals?.[`${range}_directions_rate`] ?? 0;
  const trucks = data?.trucks || [];
  // Hide fully-zero trucks from the breakdown table for the selected range
  // so the view stays clean when only one truck has traffic.
  const activeTrucks = trucks.filter((t) => {
    const c = t[range] || {};
    return (c.profile_view || 0) + (c.pin_click || 0) + (c.directions_click || 0)
      + (c.menu_view || 0) + (c.contact_inquiry || 0) + (c.event_inquiry || 0) > 0;
  });

  return (
    <section
      className="rounded-2xl border border-border bg-card p-5 sm:p-6"
      data-testid="owner-analytics-card"
    >
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-primary font-semibold">
            <ChartLineUp weight="fill" className="h-3.5 w-3.5" />
            Analytics
          </div>
          <h2 className="mt-1 font-display font-bold text-2xl tracking-tight">
            How you&apos;re doing
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Only you can see this.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-full border border-border bg-background p-0.5" data-testid="analytics-range-toggle">
            {[
              { key: "today", label: "Today" },
              { key: "week", label: "This week" },
            ].map((r) => (
              <button
                key={r.key}
                type="button"
                onClick={() => setRange(r.key)}
                className={
                  "h-8 px-3 rounded-full text-xs font-medium transition " +
                  (range === r.key
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground")
                }
                data-testid={`analytics-range-${r.key}`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => load({ silent: true })}
            disabled={loading || refreshing}
            className="h-8 w-8 grid place-items-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-50"
            aria-label="Refresh analytics"
            data-testid="analytics-refresh-btn"
          >
            <ArrowsClockwise
              weight="duotone"
              className={"h-4 w-4 " + (refreshing ? "animate-spin" : "")}
            />
          </button>
        </div>
      </header>

      <div
        className={
          "grid gap-2.5 mt-5 grid-cols-2 " +
          (isPro ? "md:grid-cols-5" : "md:grid-cols-4")
        }
      >
        {(isPro ? [...METRICS, ...PRO_METRICS] : METRICS).map((m) => {
          const count = loading ? null : (totals[m.key] ?? 0);
          const spark = data?.totals?.spark?.[m.key] || [];
          const delta = data?.totals?.spark_delta_pct?.[m.key];
          const sparkTotal = spark.reduce((a, b) => a + (b || 0), 0);
          return (
            <button
              type="button"
              key={m.key}
              onClick={() => !loading && setBreakdownMetric(m)}
              disabled={loading}
              className="rounded-xl border border-border bg-background/50 p-3 flex flex-col text-left transition hover:border-primary/60 hover:bg-background focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-default"
              data-testid={`analytics-metric-${m.key}`}
              aria-label={`Show daily breakdown for ${m.label}`}
            >
              <div className="flex items-center justify-between">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  {m.label}
                </div>
                <m.icon weight="duotone" className="h-4 w-4 text-primary" />
              </div>
              <div className="flex items-end justify-between gap-2 mt-2">
                <div
                  className="font-display font-bold text-3xl tabular-nums leading-none"
                  data-testid={`analytics-count-${m.key}`}
                >
                  {count === null ? "…" : count}
                </div>
                {!loading && spark.length > 0 && (
                  <div className="text-primary/80 flex-none" data-testid={`analytics-spark-${m.key}`}>
                    <Sparkline values={spark} width={72} height={24} />
                  </div>
                )}
              </div>
              {!loading && (
                <DeltaBadge delta={delta} sparkTotal={sparkTotal} />
              )}
            </button>
          );
        })}
      </div>

      <div
        className="mt-4 rounded-xl bg-primary/10 border border-primary/20 p-4 flex items-start gap-3"
        data-testid="analytics-conversion-card"
      >
        <div className="h-9 w-9 rounded-full bg-primary/20 grid place-items-center flex-none">
          <TrendUp weight="fill" className="h-5 w-5 text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] uppercase tracking-wider text-primary font-semibold">
            Directions conversion
          </div>
          <div className="flex items-baseline gap-2 mt-0.5">
            <span
              className="font-display font-bold text-2xl tabular-nums"
              data-testid="analytics-directions-rate"
            >
              {loading ? "…" : `${rate}%`}
            </span>
            <span className="text-xs text-muted-foreground">
              of viewers tapped Get directions {range === "today" ? "today" : "this week"}
            </span>
          </div>
        </div>
      </div>

      {!loading && trucks.length > 1 && (
        <div className="mt-5">
          <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
            Per truck
          </h3>          <div className="rounded-xl border border-border overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase tracking-wider text-muted-foreground bg-muted/30">
                <tr>
                  <th className="text-left px-3 py-2">Truck</th>
                  <th className="text-right px-3 py-2">Views</th>
                  <th className="text-right px-3 py-2">Pins</th>
                  <th className="text-right px-3 py-2">Directions</th>
                  <th className="text-right px-3 py-2">Menu</th>
                  <th className="text-right px-3 py-2">Conv.</th>
                </tr>
              </thead>
              <tbody>
                {activeTrucks.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-3 py-4 text-center text-muted-foreground text-xs">
                      No activity yet for this range.
                    </td>
                  </tr>
                ) : (
                  activeTrucks.map((t) => {
                    const c = t[range] || {};
                    const convKey = `${range}_directions_rate`;
                    return (
                      <tr
                        key={t.truck_id}
                        className="border-t border-border"
                        data-testid={`analytics-truck-row-${t.truck_id}`}
                      >
                        <td className="px-3 py-2 font-medium truncate max-w-[180px]">
                          {t.truck_name}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">{c.profile_view || 0}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{c.pin_click || 0}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{c.directions_click || 0}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{c.menu_view || 0}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-primary font-medium">
                          {(t[convKey] ?? 0)}%
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <BreakdownDialog
        metric={breakdownMetric}
        onClose={() => setBreakdownMetric(null)}
        spark={breakdownMetric ? data?.totals?.spark?.[breakdownMetric.key] || [] : []}
        sparkDays={data?.totals?.spark_days || []}
        weekTotal={breakdownMetric ? data?.totals?.week?.[breakdownMetric.key] ?? 0 : 0}
      />
    </section>
  );
}

/**
 * Delta badge — compact "↑ 40%", "↓ 12%", "—", or "New" chip shown under
 * each metric tile. delta=null (no prior-period data) becomes either "New"
 * (when there IS current activity) or nothing (when everything is zero so
 * we don't add noise).
 */
function DeltaBadge({ delta, sparkTotal }) {
  if (delta === null || delta === undefined) {
    if (!sparkTotal) {
      return (
        <div className="mt-1.5 text-[10px] text-muted-foreground/60 leading-none h-3" />
      );
    }
    return (
      <div className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-semibold text-primary leading-none">
        <Sparkle weight="fill" className="h-2.5 w-2.5" />
        New this week
      </div>
    );
  }
  if (delta === 0) {
    return (
      <div className="mt-1.5 inline-flex items-center gap-1 text-[10px] text-muted-foreground leading-none">
        <Minus className="h-2.5 w-2.5" /> Flat vs last week
      </div>
    );
  }
  const up = delta > 0;
  const Icon = up ? ArrowUp : ArrowDown;
  const color = up ? "text-emerald-600 dark:text-emerald-400" : "text-rose-500";
  return (
    <div className={`mt-1.5 inline-flex items-center gap-0.5 text-[10px] font-semibold leading-none ${color}`}>
      <Icon weight="bold" className="h-2.5 w-2.5" />
      {Math.abs(delta)}%
      <span className="ml-1 text-muted-foreground font-normal">vs last week</span>
    </div>
  );
}



/**
 * Click-to-open daily breakdown of a single metric across the last 7 days.
 * Uses the sparkline + spark_days arrays that the API already returns
 * (parallel, oldest → newest), so there's no extra round-trip. Counts are
 * already deduped per session per day on the server, so each number on
 * each row is "unique session-views on that day" — never raw click totals.
 */
function BreakdownDialog({ metric, onClose, spark, sparkDays, weekTotal }) {
  if (!metric) return null;
  const weekday = (iso) => {
    try {
      // Parse as UTC to keep the label aligned with the server-side bucket.
      const [y, m, d] = iso.split("-").map((n) => parseInt(n, 10));
      const dt = new Date(Date.UTC(y, (m || 1) - 1, d || 1));
      return dt.toLocaleDateString(undefined, { weekday: "short", timeZone: "UTC" });
    } catch {
      return "";
    }
  };
  const monthDay = (iso) => {
    try {
      const [y, m, d] = iso.split("-").map((n) => parseInt(n, 10));
      const dt = new Date(Date.UTC(y, (m || 1) - 1, d || 1));
      return dt.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });
    } catch {
      return iso;
    }
  };
  const max = Math.max(1, ...spark.map((n) => n || 0));
  const todayIso = new Date().toISOString().slice(0, 10);
  return (
    <Dialog open={!!metric} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="analytics-breakdown-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <metric.icon weight="duotone" className="h-5 w-5 text-primary" />
            {metric.label} — last 7 days
          </DialogTitle>
          <DialogDescription>
            Unique session-views per day (one viewer = one count per day).
            Total this week: <span className="font-semibold text-foreground tabular-nums">{weekTotal}</span>
          </DialogDescription>
        </DialogHeader>
        <div className="mt-2 divide-y divide-border rounded-xl border border-border" data-testid="analytics-breakdown-rows">
          {sparkDays.length === 0 ? (
            <div className="px-3 py-4 text-center text-sm text-muted-foreground">
              No data for this period yet.
            </div>
          ) : (
            sparkDays.map((dt, i) => {
              const count = spark[i] || 0;
              const pct = (count / max) * 100;
              const isToday = dt === todayIso;
              return (
                <div
                  key={dt}
                  className={`px-3 py-2.5 flex items-center gap-3 ${isToday ? "bg-primary/5" : ""}`}
                  data-testid={`analytics-breakdown-row-${dt}`}
                >
                  <div className="w-16 flex-none">
                    <div className="text-xs font-semibold leading-tight">
                      {weekday(dt)}
                      {isToday && <span className="ml-1 text-[10px] text-primary">·today</span>}
                    </div>
                    <div className="text-[10px] text-muted-foreground leading-tight">
                      {monthDay(dt)}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0 h-2 rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full bg-primary transition-[width] duration-500"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <div className="w-10 text-right tabular-nums font-semibold text-sm">
                    {count}
                  </div>
                </div>
              );
            })
          )}
        </div>
        <p className="mt-3 text-[11px] text-muted-foreground leading-relaxed">
          Counts are deduplicated by visitor session per day, and your own visits to your truck are excluded automatically.
        </p>
      </DialogContent>
    </Dialog>
  );
}
